import React, { useMemo, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronRight, ChevronDown, Plus, ListChecks, Search } from "lucide-react";
import { OBJECTIVE_TYPES, FACT_TYPES, LAW_TYPES, deriveStatus, humanize } from "./proofApi";
import { StatusPill, VerificationChip, TypeTag, Pill } from "./chips";

const DEPTHS = [["issues", "Issues"], ["requirements", "Requirements"], ["propositions", "Propositions"], ["sources", "Sources"]];
const RANK = { issues: 1, requirements: 2, propositions: 3, sources: 4 };
const LEVEL_OF = { RELIEF: 0, CLAIM: 0, DEFENCE: 0, ISSUE: 1, LEGAL_REQUIREMENT: 2 };

export function ProofBoard({ G, nodes, gaps, side, search, onInspect, onAddChild, onOpenGaps }) {
  const [depth, setDepth] = useState("propositions");
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const gapsByNode = useMemo(() => gaps.reduce((a, g) => { if (g.affectedNodeId) (a[g.affectedNodeId] ||= []).push(g); return a; }, {}), [gaps]);
  const q = (search || "").toLowerCase();

  const matchSide = (n) => side === "BOTH" || (side === "OURS" ? n.side !== "OPPONENT" : n.side === "OPPONENT");
  const matchSearch = (n) => !q || n.label.toLowerCase().includes(q) || (n.summary || "").toLowerCase().includes(q) || (n.sourceRefs || []).some((s) => (s.label || s.locator || "").toLowerCase().includes(q));

  // Build hierarchical rows: objectives -> issues -> requirements -> propositions (-> sources at depth 4)
  const rows = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (n, level, kind) => { out.push({ node: n, level, kind }); seen.add(n.id); };
    const walk = (n, level) => {
      if (n.status !== "ACTIVE" && n.status !== "PROPOSED") return;
      push(n, level, "node");
      if (collapsed[n.id]) return;
      const kids = G.children(n.id).filter((k) => LAW_TYPES.includes(k.nodeType) || FACT_TYPES.includes(k.nodeType));
      kids.forEach((k) => {
        const lvl = LEVEL_OF[k.nodeType] ?? 3;
        if (FACT_TYPES.includes(k.nodeType) && RANK[depth] < 3) return;
        if (k.nodeType === "LEGAL_REQUIREMENT" && RANK[depth] < 2) return;
        walk(k, Math.max(level + 1, lvl));
      });
      if (RANK[depth] >= 4 && (FACT_TYPES.includes(n.nodeType) || n.nodeType === "LEGAL_REQUIREMENT")) {
        G.supporters(n.id).forEach((s) => out.push({ node: s, level: level + 1, kind: "support" }));
        G.attackers(n.id).forEach((s) => out.push({ node: s, level: level + 1, kind: "challenge" }));
      }
    };
    nodes.filter((n) => OBJECTIVE_TYPES.includes(n.nodeType) && (n.status === "ACTIVE" || n.status === "PROPOSED") && matchSide(n)).forEach((n) => walk(n, 0));
    // Orphan requirements / issues not reachable from an objective
    const orphans = nodes.filter((n) => LAW_TYPES.includes(n.nodeType) && !seen.has(n.id) && (n.status === "ACTIVE" || n.status === "PROPOSED") && !G.parents(n.id).length && matchSide(n));
    if (orphans.length) { out.push({ group: "Unlinked issues & requirements" }); orphans.forEach((n) => walk(n, 1)); }
    if (!q) return out;
    // Keep rows that match search, plus their ancestors (by level walk-back)
    const keep = new Set();
    out.forEach((r, i) => { if (r.node && matchSearch(r.node)) { keep.add(i); let lvl = r.level; for (let j = i - 1; j >= 0 && lvl > 0; j--) { if (out[j].node && out[j].level < lvl) { keep.add(j); lvl = out[j].level; } } } });
    return out.filter((_, i) => keep.has(i));
    // eslint-disable-next-line
  }, [nodes, G, side, depth, collapsed, q]);

  if (!rows.length) return <EmptyState icon={ListChecks} title="No requirements have been mapped yet." message={q ? "Nothing matches your search." : "Add a relief, issue or requirement to begin the proof structure."} actionLabel={q ? undefined : "Add Requirement"} onAction={q ? undefined : () => onAddChild(null)} />;

  const cell = (n) => {
    const pleading = [...G.pleadings(n.id).map((p) => p.label), ...(n.sourceRefs || []).filter((s) => s.type === "pleading").map((s) => s.label || s.locator)];
    const opp = G.attackers(n.id).filter((a) => a.side === "OPPONENT");
    const supp = G.supporters(n.id);
    const wit = G.witnesses(n.id);
    const auth = G.authorities(n.id);
    const found = supp.map((s) => s.exhibitStatus).filter(Boolean);
    return { pleading, opp, supp, wit, auth, found };
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-xs text-ink-2">Expand to:
          {DEPTHS.map(([k, l]) => <button key={k} onClick={() => setDepth(k)} className={cn("rounded-full px-2.5 py-1", depth === k ? "bg-navy text-white" : "border border-line hover:border-copper/40")} data-testid={`pg-depth-${k}`}>{l}</button>)}
        </div>
        <Button size="sm" variant="outline" onClick={() => onAddChild(null)} data-testid="pg-add-requirement"><Plus className="mr-1 h-3.5 w-3.5" /> Add Requirement</Button>
      </div>

      {/* Desktop matrix */}
      <Panel className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[1080px] text-sm" data-testid="pg-board-table">
          <thead><tr className="border-b border-line bg-surface text-left text-xs uppercase tracking-wide text-ink-muted">
            {["Requirement / Proposition", "Our Pleading", "Opponent Position", "Record Support", "Witness", "Exhibit / Foundation", "Authority", "Status", ""].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              if (r.group) return <tr key={`g${i}`} className="bg-surface"><td colSpan={9} className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-muted">{r.group}</td></tr>;
              const n = r.node; const c = cell(n); const st = deriveStatus(n, G); const ng = gapsByNode[n.id] || [];
              const hasKids = G.children(n.id).length > 0;
              const isObjective = OBJECTIVE_TYPES.includes(n.nodeType);
              return (
                <tr key={`${n.id}-${r.kind}-${i}`} className={cn("border-b border-line last:border-0 hover:bg-surface/60", n.status === "PROPOSED" && "bg-info-bg/40", isObjective && "bg-surface/40")} data-testid={`pg-row-${n.id}`}>
                  <td className="px-3 py-2" style={{ paddingLeft: `${12 + r.level * 18}px` }}>
                    <div className="flex items-start gap-1.5">
                      {hasKids && r.kind === "node" ? <button onClick={() => toggle(n.id)} className="mt-0.5 text-ink-muted" aria-label={collapsed[n.id] ? "Expand" : "Collapse"}>{collapsed[n.id] ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button> : <span className="w-4" />}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <TypeTag type={n.nodeType} />
                          {r.kind === "support" && <Pill tone="success">supports</Pill>}
                          {r.kind === "challenge" && <Pill tone="danger">{humanize(n.edge?.relationType)}</Pill>}
                          {n.side === "OPPONENT" && <Pill tone="muted">Opponent</Pill>}
                          {n.status === "PROPOSED" && <Pill tone="info">Proposed</Pill>}
                        </div>
                        <button onClick={() => onInspect(n)} className={cn("mt-0.5 text-left text-ink hover:text-copper", isObjective && "font-display text-base font-semibold")} data-testid={`pg-node-${n.id}`}>{n.label}</button>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-2">{c.pleading.length ? c.pleading.slice(0, 2).join("; ") : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-ink-2">{c.opp.length ? c.opp.slice(0, 2).map((o) => <div key={o.id} className="truncate max-w-[200px]" title={o.label}>{o.label}</div>) : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-ink-2">{c.supp.length ? c.supp.slice(0, 3).map((s) => <div key={s.id} className="truncate max-w-[200px]" title={s.label}>{s.label}</div>) : (FACT_TYPES.includes(n.nodeType) || n.nodeType === "LEGAL_REQUIREMENT") ? <span className="text-danger">No support linked</span> : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-ink-2">{c.wit.length ? c.wit.map((w) => w.label).join(", ") : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-ink-2">{n.exhibitStatus ? humanize(n.exhibitStatus) : c.found.length ? [...new Set(c.found)].map(humanize).join(", ") : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-ink-2">{c.auth.length ? c.auth.map((a) => <div key={a.id} className="truncate max-w-[180px]" title={a.label}>{a.label}</div>) : n.nodeType === "LEGAL_REQUIREMENT" ? <span className="text-warning">None linked</span> : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-3 py-2"><div className="flex flex-col items-start gap-1">{st !== "NOT_APPLICABLE" && <StatusPill status={st} data-testid={`pg-status-${n.id}`} />}{(n.nodeType === "LEGAL_REQUIREMENT" || n.nodeType === "AUTHORITY") && <VerificationChip node={n} />}</div></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {ng.length > 0 && <button onClick={() => onOpenGaps(n)} className="mr-2 rounded-full bg-warning-bg px-2 py-0.5 text-[11px] font-medium text-warning" data-testid={`pg-gapcount-${n.id}`}>{ng.length} gap{ng.length > 1 ? "s" : ""}</button>}
                    {(isObjective || LAW_TYPES.includes(n.nodeType)) && <button onClick={() => onAddChild(n)} className="text-xs text-copper hover:underline" data-testid={`pg-addchild-${n.id}`}>+ Add</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden" data-testid="pg-board-cards">
        {rows.filter((r) => r.node && (r.node.nodeType === "LEGAL_REQUIREMENT" || OBJECTIVE_TYPES.includes(r.node.nodeType))).map((r) => {
          const n = r.node; const st = deriveStatus(n, G); const ng = gapsByNode[n.id] || [];
          return (
            <button key={n.id} onClick={() => onInspect(n)} className="w-full rounded-xl border border-line bg-card p-3 text-left">
              <div className="flex items-center justify-between gap-2"><TypeTag type={n.nodeType} />{st !== "NOT_APPLICABLE" && <StatusPill status={st} />}</div>
              <div className="mt-1 text-sm font-medium text-ink">{n.label}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-ink-2">
                <span>{G.supporters(n.id).length + G.children(n.id).reduce((a, k) => a + G.supporters(k.id).length, 0)} support</span>
                <span>{G.attackers(n.id).length + G.children(n.id).reduce((a, k) => a + G.attackers(k.id).length, 0)} conflicts</span>
                <span className={ng.length ? "text-warning" : ""}>{ng.length} gaps</span>
              </div>
            </button>
          );
        })}
      </div>
      {q && <div className="mt-2 flex items-center gap-1 text-xs text-ink-muted"><Search className="h-3 w-3" /> Filtered by “{search}”</div>}
    </div>
  );
}
